import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { observer } from "mobx-react-lite";
import { HomeContainer } from "@client/pages/Home/Home.styled";
import { GlobalLoader } from "@common/components/GlobalLoader/GlobalLoader";
import { ConfirmActionModal } from "@common/components/Modal/ConfirmActionModal/ConfirmActionModal";
import { useStores } from "@client/store";
import {
  Block,
  Flex,
  Spacer,
  TabSection,
  Typography,
  useTheme,
} from "@ugc-ui/lego";
import {
  CreateCampaignsControl,
  CreateCampaignTabs,
} from "@admin/pages/CreateCampaign/CreateCampaign.styled";
import { ShowCampaignWrapper } from "@client/pages/ShowCampaign/ShowCampaign.styled";
import { useIsMobile } from "@common/hooks/isMobile";
import { CreateCampaignGroup } from "@client/pages/CreateCampaign/CreateCampaign";
import { useHorizontalScroll } from "@common/hooks/useHorizontalScroll";
import { ShowCampaignTabStatistic } from "@client/pages/ShowCampaign/Tabs/ShowCampaignTabStatistic";
import { TabViewShowCampaignTabView } from "@client/pages/ShowCampaign/Tabs/ShowCampaignTabView";
import { getStatusColor } from "@common/utils/getStatusColor";
import { Header } from "@client/pages/Home/Header/Header";
import { ButtonBack } from "@common/components/ButtonBack/ButtonBack";
import { InitialDialog } from "@client/pages/Home/InitialDialog/InitialDialog";
import { useParams } from "react-router-dom";
import { ViewDesktopSkeletonPage } from "@common/components/TabView/ViewDesktopSkeletonPage";
import { ViewTouchSkeletonPage } from "@common/components/TabView/ViewTouchSkeletonPage";
import { ErrorToHome } from "@common/components/Errors/ErrorToHome";

export enum ShowCampaignGroup {
  VIEW = "view",
  STATISTIC = "statistic",
}

export const ShowCampaignPage = observer(() => {
  const theme = useTheme();
  const isMobile = useIsMobile();
  const { exitModalStore } = useStores();

  const {
    client: { showCampaignDialogStore },
  } = useStores();

  const { attach, detach, campaignData, errorMessage } =
    showCampaignDialogStore;

  const { uuid } = useParams();

  useEffect(() => {
    attach(uuid);
    return detach;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const ref = useHorizontalScroll<HTMLDivElement>();

  const [tab, setTab] = useState(CreateCampaignGroup.VIEW);
  const handleClickOnCampaignTab = (newTab) => setTab(newTab);

  if (errorMessage) {
    return <ErrorToHome message={errorMessage} />;
  }

  const skeletonPage = isMobile ? (
    <ViewTouchSkeletonPage />
  ) : (
    <ViewDesktopSkeletonPage />
  );

  return (
    <>
      <Helmet>
        <title>Рекламный кабинет - просмотр кампании</title>
      </Helmet>
      {!isMobile && <Header />}
      <Block dataCy="страница просмотра рекламной кампании">
        <HomeContainer>
          {campaignData ? (
            <ShowCampaignWrapper>
              <div>
                <ButtonBack
                  title={showCampaignDialogStore.campaignData?.name}
                  exitModalStore={exitModalStore}
                />
                <CreateCampaignsControl theme={theme}>
                  <CreateCampaignTabs
                    ref={ref}
                    dataCy="контейнер-табов"
                    value={tab}
                    onChange={handleClickOnCampaignTab}
                    padding="0"
                    underlineHeight={1}
                  >
                    <TabSection label="Обзор" value={ShowCampaignGroup.VIEW} />
                    <TabSection
                      label="Статистика"
                      value={ShowCampaignGroup.STATISTIC}
                    />
                  </CreateCampaignTabs>

                  {!isMobile && (
                    <Typography
                      type="subtitle2"
                      dataCy="заголовок страницы кампании"
                      color={getStatusColor(
                        theme,
                        showCampaignDialogStore.campaignData.status
                      )}
                    >
                      {showCampaignDialogStore.campaignData.status.name}
                    </Typography>
                  )}
                </CreateCampaignsControl>
              </div>
              <Spacer size={28} vertical />
              {tab === CreateCampaignGroup.VIEW ? (
                <TabViewShowCampaignTabView />
              ) : (
                <ShowCampaignTabStatistic
                  campaign={showCampaignDialogStore.campaignData}
                />
              )}
              <Flex />
            </ShowCampaignWrapper>
          ) : (
            skeletonPage
          )}
        </HomeContainer>
      </Block>
      <GlobalLoader />
      <ConfirmActionModal exitModalStore={exitModalStore} />
      <InitialDialog />
    </>
  );
});
